// client/src/components/shell/PfShell.js
//
// The team-detail viewport wrapper. Constrains content to mobile
// width, applies the page background wash, reserves clearance at
// top and bottom for TopBar and TabBar.
//
// Structure:
//   [TopBar]          ← fixed to top of the frame (60px tall-ish)
//   [screen content]  ← scrollable middle, with screenX padding
//   [TabBar]          ← fixed to bottom, 110px reserved via padding
//
// The wrapper is always a phone-width column. On desktop that means
// it's centered on-screen with the page background wrapping around
// it. On real phones it just fills the viewport.
//
// showChrome={false} skips the TopBar + TabBar entirely. Use for
// pages that don't belong to a team (login, org picker, /kitchen-sink).

export function PfShell({
  children,
  showChrome = true,
  topBar,
  tabBar,
  className = '',
}) {
  return (
    // Outer full-viewport container. This is the "everything outside
    // the phone frame" area — on desktop, a wash of bg-bg around the
    // centered phone. min-h-screen so short pages still fill the view.
    <div className="min-h-screen bg-bg flex justify-center">
      {/* The phone frame itself. max-w-md caps it at ~448px on desktop.
          bg-bg here is redundant with the outer wrapper, but explicit
          so this component works standalone if ever mounted without a
          wrapping bg. relative because TopBar and TabBar position
          against it. */}
      <div
        className={[
          'relative w-full max-w-md bg-bg',
          // Reserve space for TopBar and TabBar via padding when chrome
          // is showing. When chrome is hidden, no reservation needed.
          showChrome ? 'pt-14 pb-[110px]' : '',
          className,
        ].filter(Boolean).join(' ')}
      >
        {/* Top chrome. Renders the provided topBar prop, or a
            placeholder if none supplied. */}
        {showChrome && (
          topBar ?? (
            <div className="absolute top-0 left-0 right-0 h-14 bg-paper border-b border-rule flex items-center justify-center">
              <span className="text-eyebrow uppercase text-inkSub">
                TopBar goes here
              </span>
            </div>
          )
        )}

        {/* The screen content. screenX from our design tokens gives
            22px horizontal padding on both sides — matches HANDOFF §2. */}
        <div className="px-screenX">
          {children}
        </div>

       {/* Bottom chrome. Renders the provided tabBar prop, or a
            placeholder if none supplied. */}
        {showChrome && (
          tabBar ?? (
            <div className="absolute bottom-0 left-0 right-0 h-[110px] bg-paper border-t border-rule flex items-center justify-center">
              <span className="text-eyebrow uppercase text-inkSub">
                TabBar goes here
              </span>
            </div>
          )
        )}
      </div>
    </div>
  );
}